import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from 'react';

import { useAuth } from './AuthContext';
import { userAxios } from '../utils/api';
import { config } from '../config/config';
import { logger } from '../utils/SiteHelpers';

const SubscriptionAccessContext = createContext();

/**
 * Provides a context for checking if a user has subscription access.
 *
 * @typedef {Object} SubscriptionAccessProviderProps
 * @property {ReactNode} children The children components of the provider.
 *
 * @returns {ReactElement} The component that provides the context.
 */
export const SubscriptionAccessProvider = ({ children }) => {
    const { userId, user, accessToken } = useAuth();
    const { subscriptionUrl } = config;
    const [loading, setLoading] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState(null);

    const fetchSubscriptionAccess = useCallback(async () => {
        setLoading(true);
        try {
            if (!user || !accessToken) return;

            const url = `${subscriptionUrl}access`;
            const payload = { user_id: userId };

            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            };

            const response = await userAxios.post(url, payload, { headers });

            if (response?.data?.data?.length) {
                const data = response.data.data[0];

                const endDate = new Date(data.end_datetime);
                const daysLeft = Math.ceil(
                    (endDate.getTime() - new Date().getTime()) /
                        (1000 * 60 * 60 * 24),
                );

                const normalizedName = data.subscription_name
                    .split('Account')
                    .join('')
                    .trim();

                const isTrial = normalizedName === 'Trial';
                const isMonthly = normalizedName === 'Monthly';
                const isAnnual = normalizedName === 'Annual';

                setSubscriptionData({
                    subscriptionId: data.subscription_id,
                    subscriptionName: data.subscription_name,
                    endDate: endDate.toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                    }),
                    agreementId: data.agreement_id,
                    paymentMethod: data.payment_method,
                    daysLeft,
                    normalizedName,
                    isTrial,
                    isMonthly,
                    isAnnual,
                    isPaid: response.data.isPaid,
                });
            }
        } catch (error) {
            logger.error('Error fetching subscription access:', error);
        } finally {
            setLoading(false);
        }
    }, [user, accessToken, userId]);

    useEffect(() => {
        fetchSubscriptionAccess();
    }, [fetchSubscriptionAccess]);

    return (
        <SubscriptionAccessContext.Provider
            value={{
                loading,
                setLoading,
                subscriptionData,
            }}
        >
            {children}
        </SubscriptionAccessContext.Provider>
    );
};

export const useSubscriptionAccess = () =>
    useContext(SubscriptionAccessContext);
